"use client";

import { Role } from "@prisma/client";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { JSX, useEffect } from "react";
import { Components } from "@/app/(components)/_components/Components";
import IconArrowLeft from "@/app/(components)/generics/Icons/IconArrowLeft";
import IconDashboard from "@/app/(components)/generics/Icons/IconDashboard";
import IconInsoleLeft from "@/app/(components)/generics/Icons/IconInsoleLeft";
import IconLibrary from "@/app/(components)/generics/Icons/IconLibrary";
import IconShield from "@/app/(components)/generics/Icons/IconShield";
import IconSidebarAlt from "@/app/(components)/generics/Icons/IconSidebarAlt";
import IconStethoscope from "@/app/(components)/generics/Icons/IconStethoscope";
import IconUser from "@/app/(components)/generics/Icons/IconUser";
import IconUsers from "@/app/(components)/generics/Icons/IconUsers";
import Logo from "@/app/(components)/generics/Logo/Logo";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import { Tooltip, TooltipPosition } from "@/app/(components)/generics/Tooltip/Tooltip";
import { ConfiguratorStore } from "@/app/stores/ConfiguratorStore";
import { ModalStore } from "@/app/stores/ModalStore";
import { SidebarStore } from "@/app/stores/SidebarStore";
import { UserStore } from "@/app/stores/UserStore";
import { ViewStore } from "@/app/stores/ViewStore";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { DefaultProps } from "@/app/types/DefaultProps";
import { UStyles } from "@/app/utils/UStyles";
import { RouteSegments, Routes } from "@/app/vars/Routes";
import SidebarAccount from "./components/SidebarAccount/SidebarAccount";
import styles from "./Sidebar.module.css";

type NavLink = {
  href: string;
  label: string | TKey;
  Icon: () => JSX.Element;
};

type NavSection = {
  label: string | TKey;
  links: NavLink[];
};

type Props = DefaultProps & {};

export default function Sidebar(props: Props) {
  const { lang } = props.params;

  const isOpen = SidebarStore((state) => state.isOpen);
  const onInit = SidebarStore((state) => state.onInit);
  const toggleSidebar = SidebarStore((state) => state.toggleSidebar);
  const closeSidebarOnMobile = SidebarStore((state) => state.closeSidebarOnMobile);

  const user = UserStore((state) => state.user);
  const isAdmin = user?.role === Role.ADMIN;

  const isAdminView = ViewStore((state) => state.isAdminView);
  const toggleView = ViewStore((state) => state.toggleView);
  const onInitView = ViewStore((state) => state.onInit);

  const configPatient = ConfiguratorStore((state) => state.patient);
  const isModelMode = ConfiguratorStore((state) => state.isModelMode);
  const configModel = ConfiguratorStore((state) => state.model);

  const router = useRouter();
  const pathname = usePathname();

  const isConfigurator = pathname.endsWith(RouteSegments.CONFIGURATOR);
  const collapsed = isConfigurator ? true : !isOpen;
  const expanded = !collapsed;

  useEffect(() => {
    onInit();
    onInitView();
  }, [onInit, onInitView]);

  useEffect(() => {
    const onResize = () => {
      document.body.style.overflow = expanded && window.innerWidth < 992 ? "hidden" : "auto";
    };

    onResize();

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [expanded]);

  const normalize = (path: string) => path.replace(/\/$/, "") || "/";
  const toPath = (href: string) =>
    normalize(href.startsWith("http") ? new URL(href).pathname : href);
  const currentPath = normalize(pathname);

  const switchView = (toAdmin: boolean) => {
    if (toAdmin === isAdminView) {
      return;
    }

    toggleView();
    router.replace(Routes.ROOT(lang));
  };

  const openPatientInfo = () => {
    if (configPatient) {
      ModalStore.getState().openPatientInfo(lang, configPatient, (patient) => {
        ModalStore.getState().openEditPatient(lang, patient, () => {
          router.refresh();
        });
      });
    }
  };

  const openModelDetail = () => {
    if (!configModel) {
      return;
    }

    ModalStore.getState().openModal(
      Components.EDIT_INSOLE_MODEL,
      T(TKey.EDIT_INSOLE_MODEL, lang),
      {
        lang,
        model: configModel,
        onSuccess: () => {
          ModalStore.getState().closeModal();
          router.refresh();
        },
        onDelete: () => {
          ModalStore.getState().closeModal();
          router.push(Routes.PROTECTED.LIBRARY(lang));
        },
      },
      ModalType.MEDIUM,
    );
  };

  const sections: NavSection[] = [
    {
      label: T(TKey.GENERAL, lang),
      links: [
        {
          href: Routes.ROOT(lang),
          label: T(TKey.OVERVIEW, lang),
          Icon: IconDashboard,
        },
        ...(isAdmin && isAdminView
          ? [
              {
                href: Routes.PROTECTED.ADMIN.USERS(lang),
                label: T(TKey.USERS, lang),
                Icon: IconUsers,
              },
            ]
          : [
              {
                href: Routes.PROTECTED.PRO.PATIENTS(lang),
                label: T(TKey.PATIENTS, lang),
                Icon: IconUsers,
              },
            ]),
        {
          href: Routes.PROTECTED.LIBRARY(lang),
          label: T(TKey.LIBRARY, lang),
          Icon: IconLibrary,
        },
      ],
    },
  ];

  const renderLink = (link: NavLink) => {
    const { href, label, Icon } = link;
    const isActive = currentPath === toPath(href);

    if (expanded) {
      return (
        <Link
          key={href}
          href={href}
          className={UStyles.classNames(styles.navLink, isActive ? styles.navLinkActive : "")}
          onClick={closeSidebarOnMobile}
        >
          <Icon />
          <span>{label}</span>
        </Link>
      );
    }

    return (
      <Tooltip key={href} content={label} position={TooltipPosition.RIGHT}>
        <Link
          href={href}
          className={UStyles.classNames(
            styles.navLink,
            isActive ? styles.navLinkActive : "",
            styles.navLinkCollapsed,
          )}
          onClick={closeSidebarOnMobile}
        >
          <Icon />
        </Link>
      </Tooltip>
    );
  };

  const renderViewSwitchButton = (toAdmin: boolean) => {
    const isActive = toAdmin === isAdminView;
    const label = T(toAdmin ? TKey.ADMIN_VIEW : TKey.PRO_VIEW, lang);

    const button = (
      <button
        className={UStyles.classNames(
          styles.viewSwitchBtn,
          isActive ? styles.viewSwitchBtnActive : "",
        )}
        onClick={() => switchView(toAdmin)}
      >
        {toAdmin ? <IconShield /> : <IconStethoscope />}
        {expanded && <span>{label}</span>}
      </button>
    );

    if (expanded) {
      return button;
    }

    return (
      <Tooltip content={label} position={TooltipPosition.RIGHT}>
        {button}
      </Tooltip>
    );
  };

  const patientName = configPatient ? `${configPatient.firstName} ${configPatient.lastName}` : "";

  return (
    <div
      className={UStyles.classNames(styles.wrapper, collapsed ? styles.collapsed : "")}
      onClick={closeSidebarOnMobile}
    >
      <div
        className={UStyles.classNames(
          styles.container,
          collapsed ? styles.collapsed : "",
          isConfigurator ? styles.configurator : "",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.logoWrapper}>
            <Logo />
          </div>

          {!isConfigurator && (
            <button className={styles.toggleBtn} onClick={toggleSidebar}>
              <IconSidebarAlt />
            </button>
          )}
        </div>

        {isConfigurator && (
          <div className={styles.backWrapper}>
            <Tooltip
              content={T(isModelMode ? TKey.BACK_TO_LIBRARY : TKey.BACK_TO_PATIENTS, lang)}
              position={TooltipPosition.RIGHT}
            >
              <Link
                href={
                  isModelMode ? Routes.PROTECTED.LIBRARY(lang) : Routes.PROTECTED.PRO.PATIENTS(lang)
                }
                className={styles.backBtn}
              >
                <IconArrowLeft />
                {expanded && (
                  <span>{T(isModelMode ? TKey.BACK_TO_LIBRARY : TKey.BACK_TO_PATIENTS, lang)}</span>
                )}
              </Link>
            </Tooltip>
          </div>
        )}

        <nav className={styles.nav}>
          {isConfigurator && configModel && (
            <Tooltip content={configModel.name} position={TooltipPosition.RIGHT}>
              <button
                className={UStyles.classNames(
                  styles.navLink,
                  collapsed ? styles.navLinkCollapsed : "",
                )}
                onClick={openModelDetail}
              >
                <IconInsoleLeft />
                {expanded && <span>{configModel.name}</span>}
              </button>
            </Tooltip>
          )}

          {isConfigurator && !configModel && configPatient && (
            <Tooltip content={patientName} position={TooltipPosition.RIGHT}>
              <button
                className={UStyles.classNames(
                  styles.navLink,
                  collapsed ? styles.navLinkCollapsed : "",
                )}
                onClick={openPatientInfo}
              >
                <IconUser />
                {expanded && <span>{patientName}</span>}
              </button>
            </Tooltip>
          )}

          {!isConfigurator &&
            sections.map((section) => (
              <div key={String(section.label)} className={styles.navSectionBlock}>
                {section.links.map(renderLink)}
              </div>
            ))}
        </nav>

        <div className={styles.footer}>
          {!isConfigurator && isAdmin && (
            <div
              className={UStyles.classNames(
                styles.viewSwitch,
                collapsed ? styles.viewSwitchCollapsed : "",
              )}
            >
              {renderViewSwitchButton(true)}
              {renderViewSwitchButton(false)}
            </div>
          )}

          {user && <SidebarAccount user={user} lang={lang} isCollapsed={collapsed} />}
        </div>
      </div>
    </div>
  );
}
